import React, { useEffect, useMemo } from 'react';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import {
	useEntryPoint,
	type UseEntryPoint,
} from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { asyncTransitionBenefitsModal } from '@atlassian/jira-issue-transition-benefits-modal/src/entrypoint.tsx';
import { useSubscribeOpenIssueTransitionModalPubSub } from '@atlassian/jira-issue-transition-trigger/src/controllers/open-issue-transition-modal-pub-sub/use-subscribe-open-issue-transition-modal-pub-sub.tsx';
import { useTriggerIssueTransitionModal } from '@atlassian/jira-issue-transition-trigger/src/utils/use-trigger-issue-transition-modal/index.tsx';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	useProjectContext,
	useProjectKey,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	ISSUE_TRANSITION_VIEW_TYPE,
	ISSUE_TRANSITION_AGENT_VIEW_TYPE,
} from '@atlassian/jira-ui-modifications-view-issue-transition/src/common/constants/index.tsx';
import type { Variables } from './types.tsx';
import { asyncIssueTransitionModal } from './ui/entrypoint.tsx';

// this is just a place holder, if issueId is not available, we will not use issue ari but instead use the issue Key.
// How ever relay need this to be defined, so we are using a default value.
const DEFAULT_ISSUE_ARI = 'ari:cloud:jira::issue/';
const runtimeProps = {};

export const AsyncIssueTransitionModal = (): React.JSX.Element => {
	const [{ isModalOpen, payload, isBenefitsModalOpen }] = useTriggerIssueTransitionModal();
	const { transitionId, issueId, issueKey } = payload;
	/**
	 * Creating ari's which may be incorrect.
	 * This is not used in the query apart from verifying issueId,
	 */
	const cloudId = useCloudId();
	const defautIssueAri = DEFAULT_ISSUE_ARI;
	const updatedIssueAriId = issueId ?? defautIssueAri;
	/* End of code which should not be used for any other thing
	 */

	// Get project info to determine projectType
	const projectKey = useProjectKey(issueKey || '');
	const project = useProjectContext(projectKey);
	const projectType = project?.projectType?.toLowerCase();

	const isAgentViewAndUIMEnabled = useMemo(
		() => projectType === SERVICE_DESK_PROJECT && fg('enable_ui_modifications_for_it_agent_view'),
		[projectType],
	);

	const viewType = isAgentViewAndUIMEnabled
		? ISSUE_TRANSITION_AGENT_VIEW_TYPE
		: ISSUE_TRANSITION_VIEW_TYPE;

	const variables: Variables = useMemo(() => {
		const shouldUseIssueKey = !issueId;
		const issueInput = shouldUseIssueKey ? { issueKey } : { issueId };
		return {
			transitionId,
			issueId: updatedIssueAriId,
			issueKey,
			cloudId,
			moveGicFieldToDecomp: true,
			useForgeExtensionsWithModuleId: fg('forge-extensions-module-id-for-relay'),
			shouldUseIssueKey: !issueId,
			issueInput,
			forgeExtensionRenderingContext: {
				issueKey,
			},
			uiModificationsContext: {
				issueKey,
				viewType,
			},
		};
	}, [transitionId, updatedIssueAriId, issueKey, cloudId, issueId, viewType]);

	const {
		entryPointActions,
		entryPointReferenceSubject,
	}: UseEntryPoint<typeof asyncIssueTransitionModal> = useEntryPoint<
		typeof asyncIssueTransitionModal
	>(asyncIssueTransitionModal, variables);

	useSubscribeOpenIssueTransitionModalPubSub();

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isModalOpen && transitionId && (issueKey || issueId)) {
			entryPointActions.load();
		} else {
			entryPointActions.unload();
		}
	}, [entryPointActions, isModalOpen, issueKey, issueId, transitionId]);

	const {
		entryPointActions: benefitsModalentryPointActions,
		entryPointReferenceSubject: benefitsModalEntryPointReferenceSubject,
	}: UseEntryPoint<typeof asyncTransitionBenefitsModal> = useEntryPoint<
		typeof asyncTransitionBenefitsModal
	>(asyncTransitionBenefitsModal, {});
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (!fg('modern_issue_transition_enable_benefits_modal')) return;
		if (isBenefitsModalOpen) {
			benefitsModalentryPointActions.load();
		} else {
			benefitsModalentryPointActions.unload();
		}
	}, [benefitsModalentryPointActions, isBenefitsModalOpen]);
	return (
		<>
			<ModalEntryPointContainer
				entryPointReferenceSubject={benefitsModalEntryPointReferenceSubject}
				id="async.jira.benefits-transition-modal"
				packageName="jiraIssueTransitionModal"
				teamName="Gryffindor"
				runtimeProps={runtimeProps}
				entryPointActions={benefitsModalentryPointActions}
				errorFallback="flag"
			/>
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="async.jira.issue-transition-modal"
				packageName="jiraIssueTransitionModal"
				teamName="Gryffindor"
				runtimeProps={runtimeProps}
				entryPointActions={entryPointActions}
				errorFallback="flag"
			/>
		</>
	);
};
